import { format } from "date-fns";
import { find, isNil } from "ramda";
import { Fragment } from "react";
import { TooltipProps } from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import i18n, { Language, languageToLocale } from "../../../i18n/i18n";
import { ChartLegend, ChartLegendProps } from "./ChartLegend";

export type ChartTooltipProps = ChartLegendProps & {
  dataKey: string;
  unit?: string; //ParameterUnit
  regulationName?: string;
};

export const labelByXType = (
  xType: "date" | "day" | "hour" | "other",
  label: number | string
) =>
  label
    ? xType === "date"
      ? format(label, "d MMM HH:mm", {
          locale: languageToLocale[i18n.language as Language],
        })
      : xType === "day"
        ? i18n.t("day") + " " + label
        : xType === "hour"
          ? label + "h"
          : label
    : "";

export const findValueInPayload = (
  payload: TooltipProps<ValueType, NameType>["payload"],
  dataKey: string
) => payload && (find((p) => p.dataKey === dataKey, payload)?.value as number);

export const ChartTooltip = ({
  label,
  payload,
  displayProps,
  xType = "date",
  extraLabel = "",
  labelUnit = "",
}: TooltipProps<ValueType, NameType> & {
  displayProps: ChartTooltipProps[];
  xType?: "day" | "date" | "hour" | "other";
  extraLabel?: string;
  labelUnit?: string;
  fixedDigits?: number;
}) => {
  if (!payload) {
    return <></>;
  } else {
    return (
      <div className="bg-white border-grey-light border-2 rounded-lg text-lg fcen flex-col p-2 text-high">
        <div className="w-full fcen flex-col">
          <span>{label ? labelByXType(xType, label) + labelUnit : ""}</span>
          <span className="text-med">{extraLabel}</span>
        </div>
        <div>
          {displayProps.map((data: ChartTooltipProps, i) => {
            const value = findValueInPayload(payload, data.dataKey);
            if (data && !isNil(value)) {
              return (
                <div className="flex flex-col" key={data.dataKey + "_" + i}>
                  <div className="flex gap-2">
                    <ChartLegend
                      icon={data.icon}
                      color={data.color ? data.color : ""}
                      strokeDashArray={data.strokeDashArray}
                      text={data.text}
                    />
                    {data.unit ? value + "" + data.unit : value}
                  </div>
                </div>
              );
            } else {
              return <Fragment key={data.dataKey + "_" + i} />;
            }
          })}
        </div>
      </div>
    );
  }
};
